import { CurrencyOptions, CutlistRequestType } from '@cutr/constants/cutlist';
import { Features } from '@cutr/constants/cutlist-theme';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useAgentLoggedIn, useIsLoggedIn } from '@/api/login';
import { useMaterialGroups } from '@/api/materialsGroup';
import {
  useAggregatedPrice,
  usePricingError,
  usePricingStore,
} from '@/api/pricing';
import { useCutlistState } from '@/api/store';
import Card from '@/blocks/Card';
import { useIsAutoNestingRunning, useManualEdgebanding } from '@/hooks';
import { Button } from '@/primitives/Button';
import { Checkbox } from '@/primitives/Checkbox';
import { Alert, Icon, Loader } from '@/primitives/Icons';
import {
  getSourceConfig,
  isCutrEmbedded,
  useCurrentFeatures,
  useTicker,
} from '@/theme';
import { currencyFormatter, currencyToDisplayMap } from '@/utils/format';
import { isInIframe } from '@/utils/misc';

import { DeliverLeftoverMaterial } from './DeliverLeftoverMaterial';
import { InfoMessage } from './InfoMessage';
import styles from './Price.module.css';
import { PricingLineItems } from './PricingLineItems';

const priceOrDash = (amount: number, currency: CurrencyOptions) =>
  amount === 0
    ? `${currencyToDisplayMap[currency]} –.–`
    : currencyFormatter(amount, currency);

const MANUAL_EDGEBANDING_NOTICES: Record<
  Features['manualEdgebandingRule'],
  { key: string; variant: 'error' | 'warning' | 'default' }
> = {
  'not-allowed': {
    key: 'cutlist-summary.smallPartEdgebanding.notAllowed',
    variant: 'error',
  },
  'by-customer': {
    key: 'cutlist-summary.smallPartEdgebanding.byCustomer',
    variant: 'warning',
  },
  included: {
    key: 'cutlist-summary.smallPartEdgebanding.included',
    variant: 'default',
  },
};

export const EstimatedTotal = ({
  onAction,
  readOnly = false,
}: {
  onAction(requestType: CutlistRequestType): Promise<void>;
  readOnly?: boolean;
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const { showPricing, manualQuote, totalAmountExclVAT } = usePricingStore();
  const { t } = useTranslation();
  const [tosErrorMessage, setTosErrorMessage] = React.useState('');
  const [tosAccepted, setTosAccepted] = React.useState(false);
  const {
    requestQuoteEnabled,
    requestQuoteMinAmount,
    upfrontPayment,
    displayExpectedDelivery,
  } = useCurrentFeatures();
  const source = useTicker();
  const manualEdgebanding = useManualEdgebanding();
  const manualEdgebandingNotice =
    MANUAL_EDGEBANDING_NOTICES[manualEdgebanding.rule];

  const quoteBtnEnabled =
    requestQuoteEnabled &&
    (manualQuote || totalAmountExclVAT >= requestQuoteMinAmount);

  const showPlaceOrderButton = !manualQuote || !requestQuoteEnabled;
  const onSubmitClick = (requestType: CutlistRequestType) => {
    if (!tosAccepted) {
      setTosErrorMessage(t('common.termsAndConditionsErrorMessage'));
      setIsLoading(false);
      return;
    }
    onAction(requestType).then(() => {
      setIsLoading(false);
    });
  };

  return (
    <Card>
      {upfrontPayment && (
        <InfoMessage message={t('cutlist-summary.price.payment')} />
      )}
      {showPricing && (
        <FullPriceDataCard
          title={
            manualQuote
              ? t('cutlist-summary.price.estimated.title')
              : t('cutlist-summary.price.total.title')
          }
        />
      )}
      {!readOnly && displayExpectedDelivery && (
        <InfoMessage message={t('cutlist-summary.delivery.deliveryTime')} />
      )}

      {!readOnly && manualEdgebanding.count > 0 && (
        <InfoMessage
          variant={manualEdgebandingNotice.variant}
          message={t(manualEdgebandingNotice.key, {
            count: manualEdgebanding.count,
          })}
        />
      )}

      {source === 'STIHO' && manualQuote && (
        <Card
          variant="warning"
          style={{
            padding: '0.5rem 1rem',
            marginBottom: '1rem',
          }}
        >
          <div className="flexAlign">
            <div>
              <Icon icon={<Alert />} color="var(--amber-6)" />
            </div>
            <p>{t('cutlist-summary.manualQuoteEmailWarning')}</p>
          </div>
        </Card>
      )}

      {!readOnly && !isInIframe() && (
        <div>
          <TermsAndConditions
            tosAccepted={tosAccepted}
            setTosAccepted={setTosAccepted}
            tosErrorMessage={tosErrorMessage}
            setTosErrorMessage={setTosErrorMessage}
          />
          <p />

          {showPlaceOrderButton && (
            <Button
              name="submitPlaceOrder"
              disabled={isLoading || manualEdgebanding.isBlocked}
              onClick={() => {
                setIsLoading(true);
                onSubmitClick('place_order');
              }}
            >
              {t('cutlist-summary.price.total.placeOrder')}
            </Button>
          )}

          <p />

          {requestQuoteEnabled && (
            <Button
              name="submitRequestQuote"
              disabled={!quoteBtnEnabled || manualEdgebanding.isBlocked}
              onClick={() => onSubmitClick('request_quote')}
            >
              {t('cutlist-summary.price.total.requestQuote')}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

export const TermsAndConditions = ({
  tosAccepted,
  setTosAccepted,
  tosErrorMessage,
  setTosErrorMessage,
}: {
  tosAccepted: boolean;
  setTosAccepted: (value: boolean) => void;
  tosErrorMessage: string;
  setTosErrorMessage: (value: string) => void;
}) => {
  const { termsAndConditions, privacyPolicy } = getSourceConfig();
  return (
    <p>
      <Checkbox
        name="tosAccepted"
        checked={tosAccepted}
        onChange={(e) => {
          if (e.currentTarget.checked) {
            setTosErrorMessage('');
          }

          setTosAccepted(e.currentTarget.checked);
        }}
        errorMessage={tosErrorMessage}
      >
        <span>
          <Trans
            i18nKey="common.termsAndConditions"
            space={' '}
            values={{
              termsAndConditions,
              privacyPolicy,
            }}
          >
            I agree to the <a target="_blank">terms and conditions</a> and{' '}
            <a target="_blank">privacy policy</a>.
          </Trans>
        </span>
      </Checkbox>
    </p>
  );
};

export const EstimatedPrice = ({
  onAction,
  isNextDisabled,
}: {
  onAction(): Promise<void>;
  isNextDisabled: boolean;
}) => {
  const { showPricing, manualQuote } = usePricingStore();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);
  const isLoggedIn = useIsLoggedIn();
  const { integrationType } = useCurrentFeatures();
  const source = useTicker();
  const isAutoNestingRunning = useIsAutoNestingRunning();

  const { deliverLeftoverMaterialInput } = useCurrentFeatures();
  const materialGroups = useMaterialGroups();

  const shouldShowDeliverLeftoverMaterial =
    integrationType === 'webshop' &&
    deliverLeftoverMaterialInput &&
    materialGroups.some((group) => group.type !== 'beams');

  if (!showPricing && !onAction) return null;

  const getNextButtonText = () => {
    if (!isLoggedIn) {
      return t('cutlist-summary.price.total.unauthQuoteCTA');
    }

    if (source === 'STIHO') {
      return manualQuote
        ? t('cutlist-summary.price.total.requestQuote')
        : t('cutlist-form.alternateButtonNames.saveAndReturnToCart');
    }

    if (integrationType === 'webshop') {
      return t('cutlist-form.alternateButtonNames.saveAndReturnToCart');
    }

    return t('common.cta.next');
  };

  const disclaimer = t('cutlist-summary.price.disclaimer', {
    returnObjects: true,
    defaultValue: '',
  }) as { title: string; body: string } | '';

  const NextButton = () => {
    if (isCutrEmbedded()) {
      return null;
    }

    if (isAutoNestingRunning) {
      return (
        <Button name="theNextButton" disabled>
          <span>{t('cutlist-summary.nesting.inProgress')}</span>
          <Icon icon={<Loader />} size={2.5} />
        </Button>
      );
    }

    return (
      <Button
        name="theNextButton"
        disabled={isNextDisabled || isLoading}
        onClick={() => {
          setIsLoading(true);
          onAction().finally(() => {
            setIsLoading(false);
          });
        }}
      >
        {getNextButtonText()}
      </Button>
    );
  };

  return (
    <Card>
      {isLoggedIn && showPricing && (
        <FullPriceDataCard
          title={
            manualQuote
              ? t('cutlist-summary.price.estimated.title')
              : t('cutlist-summary.price.total.title')
          }
        />
      )}

      {shouldShowDeliverLeftoverMaterial && <DeliverLeftoverMaterial />}

      {source === 'STIHO' && !manualQuote && (
        <Card
          style={{
            padding: '0.5rem 1rem',
            marginBottom: '1rem',
            backgroundColor: 'var(--gray-4)',
          }}
        >
          <strong>{t('review.transportComputedOnCheckout')}</strong>
        </Card>
      )}
      {source === 'STIHO' && manualQuote && (
        <Card
          variant="warning"
          style={{
            padding: '0.5rem 1rem',
            marginBottom: '1rem',
          }}
        >
          <div className="flexAlign">
            <div>
              <Icon icon={<Alert />} color="var(--amber-6)" />
            </div>
            <p>{t('cutlist-summary.manualQuoteEmailWarning')}</p>
          </div>
        </Card>
      )}
      {disclaimer && (
        <Card
          style={{
            padding: '0.5rem 1rem',
            marginBottom: '1rem',
            backgroundColor: 'var(--gray-4)',
          }}
        >
          <strong>{disclaimer.title}</strong>
          <p>{disclaimer.body}</p>
        </Card>
      )}
      <NextButton />
    </Card>
  );
};

export const FullPriceDataCard = ({
  title,
  action,
  hidePrice,
}: {
  title?: string;
  action?: React.ReactNode;
  hidePrice?: boolean;
}) => {
  const { manualQuote, ...pricingStore } = usePricingStore();
  const aggregatedPriceData = useAggregatedPrice();
  const { vatDisplayMode } = useCurrentFeatures();
  const { requestQuoteEnabled, requestQuoteMinAmount } = useCurrentFeatures();
  const { vatRate } = useCutlistState();
  const { t } = useTranslation();
  const { status, currency } = useCutlistState();

  const isAgentLoggedIn = useAgentLoggedIn();
  const pricingError = usePricingError();

  const hasPriceData = Boolean(aggregatedPriceData.length) && !hidePrice;

  const totalAmountExclVAT = pricingStore.totalAmountExclVAT;
  const canRequestQuote =
    hasPriceData && totalAmountExclVAT >= requestQuoteMinAmount;

  return (
    <>
      {(vatDisplayMode === 'exclusive' || action) && (
        <div className={`opposites ${styles.header}`}>
          <div>
            <h3>{title}</h3>
            {vatDisplayMode === 'exclusive' && (
              <div className={styles.subtitle}>
                {manualQuote
                  ? t('cutlist-summary.price.estimated.subtitle')
                  : t('cutlist-summary.price.total.subtitle')}
              </div>
            )}
          </div>
          {action ?? (
            <h3 className="isPrimary" data-testid="total-order-price">
              {priceOrDash(totalAmountExclVAT / 100, currency)}
            </h3>
          )}
        </div>
      )}

      {pricingError && <InfoMessage message={pricingError} variant="error" />}

      {!pricingError && !hasPriceData && !hidePrice && (
        <p className={styles.empty}>
          {t('cutlist-summary.price.estimated.placeholder')}
        </p>
      )}

      {hasPriceData && (
        <PricingLineItems
          priceData={aggregatedPriceData}
          currency={currency}
          vatRate={vatRate}
          vatDisplayMode={vatDisplayMode}
        />
      )}

      <PriceTotals hidePrice={hidePrice} />

      {!!requestQuoteMinAmount &&
        requestQuoteEnabled &&
        status === 'draft' &&
        !isAgentLoggedIn && (
          <InfoMessage
            message={t(
              `cutlist-summary.price.total.${
                canRequestQuote
                  ? 'customQuoteCalloutAboveThreshold'
                  : 'customQuoteCalloutBelowThreshold'
              }`,
              {
                threshold: currencyFormatter(
                  requestQuoteMinAmount / 100,
                  currency
                ),
              }
            )}
          />
        )}

      {getSourceConfig().reviewCallouts?.map((key) => (
        <InfoMessage key={key} message={t(key)} />
      ))}
    </>
  );
};

const PriceTotals = ({ hidePrice }: { hidePrice?: boolean }) => {
  const pricing = usePricingStore();
  const { manualQuote } = pricing;
  const totalAmountExclVAT = hidePrice ? 0 : pricing.totalAmountExclVAT;
  const totalAmountInclVAT = hidePrice ? 0 : pricing.totalAmountInclVAT;

  const { vatDisplayMode } = useCurrentFeatures();
  const { t } = useTranslation();
  const { currency, vatRate } = useCutlistState();
  if (vatDisplayMode === 'exclusive') {
    return (
      <ul className={styles.cardPrice}>
        <li className={styles.bolder}>
          <strong>
            {manualQuote
              ? t('cutlist-summary.price.estimated.excludingVAT')
              : t('cutlist-summary.price.total.excludingVAT')}
          </strong>
          <strong>{priceOrDash(totalAmountExclVAT / 100, currency)}</strong>
        </li>
        <li style={{ height: '0.5rem' }} />
        <li className="flexAlign opposites">
          <span>
            {t('cutlist-summary.price.vatPercent', {
              vatRate,
            })}
          </span>
          <span>
            {priceOrDash(
              (totalAmountInclVAT - totalAmountExclVAT) / 100,
              currency
            )}
          </span>
        </li>

        <li className={styles.bolder}>
          <strong>
            {manualQuote
              ? t('cutlist-summary.price.estimated.includingVAT')
              : t('cutlist-summary.price.total.includingVAT')}
          </strong>
          <strong>{priceOrDash(totalAmountInclVAT / 100, currency)}</strong>
        </li>
      </ul>
    );
  }
  if (vatDisplayMode === 'inclusive') {
    return (
      <ul className={styles.cardPrice}>
        <li className={styles.bolder}>
          <strong>{t('cutlist-summary.price.total.total')}</strong>
          <strong>{priceOrDash(totalAmountInclVAT / 100, currency)}</strong>
        </li>
        <li className="flexAlign opposites">
          <span>
            {t('cutlist-summary.price.includingVatPercent', {
              vatRate,
            })}
          </span>
          <span>
            {priceOrDash(
              (totalAmountInclVAT - totalAmountExclVAT) / 100,
              currency
            )}
          </span>
        </li>
      </ul>
    );
  }
};
